import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Badge,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Tooltip,
  GlobalStyles
} from '@mui/material';
import { 
  FilterList,
  Search as SearchIcon,
  Add,
  FileDownload
} from '@mui/icons-material';

import BookingFilter from './BookingFilter';
import BookingsListView from './BookingsListView';
import BookingStatsBar from './BookingStatsBar';
import BookingStatusSidebar from './BookingStatusSidebar';
import { 
  DateRangeFilter, 
  Pagination, 
  ErrorDisplay, 
  ErrorBoundary 
} from '../../components/common';
import { useBooking } from '../../contexts/BookingContext';
import CreateNewBooking from './CreateNewBooking';
import BookingNotes from './BookingNotes';
import dayjs from 'dayjs';
import { fetchBookingFilters } from "../../services/common";
import AddBookingFinance from './AddBookingFinance';
import useErrorHandler from '../../hooks/useErrorHandler';

const defaultDateRange = {
  filter: { id: 'All', label: 'All' },
  currentStart: null,
  currentEnd: null,
  compareStart: null,
  compareEnd: null,
  all_results: true,
};



const Bookings = () => {
  const [dateRangeState, setDateRangeState] = useState(defaultDateRange);

  // View State
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [createBookingOpen, setCreateBookingOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isDetailsRoute = /^\/bookings\/\d+$/.test(location.pathname);

  const {
    bookings,
    filters: appliedFilters,
    setFilters: setAppliedFilters,
    fetchBookings,
    paginationInfo,
    loading,
    addNewBooking,
    fetchBookingStatusCounts,
    statusCounts,
    setStatusCounts
  } = useBooking();

  const [isDeleted, setIsDeleted] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Reference data
  const [referenceData, setReferenceData] = useState({
    agents: [],
    projects: [],
    leads: [],
    bookingStatusOptions: [],
    paymentStatusOptions: [],
    paymentModeOptions: [],
    units: {},
    filtered_projects: [],
    other_charges: []
  });

  // Booking-specific dialogs
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [singleNoteBookingId, setSingleNoteBookingId] = useState(null);
  const [currentAction, setCurrentAction] = useState(null);
  const [amountDialogOpen, setAmountDialogOpen] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [isFilterUpdateInProgress, setIsFilterUpdateInProgress] = useState(false);
  
  // Error handling using custom hook
  const { error, hasError, setError, clearError, handleApiError, handleAsyncOperation } = useErrorHandler({
    autoClear: false
  });
  
  // Ref to prevent multiple simultaneous API calls
  const isFetchingRef = useRef(false);

  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedSidebarStatus, setSelectedSidebarStatus] = useState('all');
  // Full project counts for sidebar: keep all projects visible even when one is selected
  const [sidebarProjectCounts, setSidebarProjectCounts] = useState({});

  // Sync deleted flag
  useEffect(() => {
    setIsDeleted(appliedFilters?.is_deleted || false);
  }, [appliedFilters]);

  // Keep sidebar project list full: when no project filter, derive counts from current bookings
  useEffect(() => {
    const hasProjectFilter = appliedFilters?.projects?.length > 0;
    const list = Array.isArray(bookings) ? bookings : [];
    if (!hasProjectFilter && list.length >= 0) {
      const map = {};
      list.forEach((b) => {
        const n = b.project?.name || 'No Project';
        const id = b.project?.id ?? null;
        if (!map[n]) map[n] = { count: 0, projectId: id };
        map[n].count += 1;
      });
      setSidebarProjectCounts(map);
    }
  }, [bookings, appliedFilters?.projects]);

  // Fetch reference data
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        clearError();
        const bookingFilters = await fetchBookingFilters();
        setReferenceData({
          agents: bookingFilters.agents || [],
          projects: bookingFilters.projects || [],
          leads: bookingFilters.leads || [],
          bookingStatusOptions: bookingFilters.booking_statuses || [],
          paymentStatusOptions: bookingFilters.payment_statuses || [],
          paymentModeOptions: bookingFilters.payment_modes || [],
          units: bookingFilters.units || {},
          filtered_projects: bookingFilters.filtered_projects || [],
          other_charges: bookingFilters.other_charges || []
        });
      } catch (error) {
        console.error('Error fetching booking filters:', error);
        handleApiError(error, 'Failed to load reference data');
        // Set empty data to prevent crashes
        setReferenceData({
          agents: [],
          projects: [],
          leads: [],
          bookingStatusOptions: [],
          paymentStatusOptions: [],
          paymentModeOptions: [],
          units: {},
          filtered_projects: [],
          other_charges: []
        });
      }
    };
    fetchReferenceData();
  }, [clearError, handleApiError]);

  // Fetch status counts
  useEffect(() => {
    const fetchStatusCounts = async () => {
      try {
        const counts = await fetchBookingStatusCounts(dateRangeState);
        setStatusCounts(counts);
      } catch (error) {
        console.error('Error fetching status counts:', error);
        // Don't show error to user for status counts, just log it
        setStatusCounts({
          all: { count: 0 },
          draft: { count: 0 },
          pending: { count: 0 },
          reserved: { count: 0 },
          confirmed: { count: 0 },
          completed: { count: 0 },
          cancelled: { count: 0 },
          expired: { count: 0 },
          deleted: { count: 0 }
        });
      }
    };
    fetchStatusCounts();
  }, [dateRangeState]);

  // Core fetch
  const fetchBookingsData = useCallback(async (searchTerm = null, customFilters = null, customDateRange = null, customPage = null, customPageSize = null, forceRefresh = false) => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current && !forceRefresh) {
      console.log('Already fetching, skipping duplicate call');
      return;
    }
    
    isFetchingRef.current = true;
    
    try {
      clearError();
      const filters = customFilters || appliedFilters;
      const dateRange = customDateRange || dateRangeState;
      console.log('dateRange', dateRange);
      const pageNum = customPage || 1;
      const pageSize = customPageSize || rowsPerPage;
      filters.all_results = dateRange.all_results;
      
      const result = await fetchBookings({ ...filters, page: pageNum, page_size: pageSize }, dateRange, searchTerm, forceRefresh);
      
      // Check if the result contains an error
      if (result && result.error) {
        console.error('API returned error:', result.message);
        handleApiError({ message: result.message, response: { status: result.status } }, 'Failed to load bookings');
        
        // If it's a server error (5xx), don't retry automatically
        if (result.status >= 500) {
          console.log('Server error detected, preventing automatic retries');
          return result;
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error fetching bookings data:', error);
      handleApiError(error, 'Failed to load bookings');
      // Return empty result to prevent crashes
      return { bookings: [], pagination: { total_count: 0, current_page: 1, total_pages: 1 } };
    } finally {
      isFetchingRef.current = false;
    }
  }, [appliedFilters, dateRangeState, rowsPerPage, fetchBookings, clearError, handleApiError]);

  // Initial load
  useEffect(() => {
    if (!isDetailsRoute) {
      fetchBookingsData(null, null, null, 1, null).catch(error => {
        console.error('Initial load error:', error);
        handleApiError(error, 'Failed to load initial data');
      });
    }
  }, [fetchBookingsData]); 


  // Separate useEffect for status counts - only when date range changes
  useEffect(() => {
    if (dateRangeState) {
      console.log('dateRangeState ', dateRangeState);
      // Status counts are not filtered by applied filters - show overall distribution
      fetchBookingStatusCounts(dateRangeState).then(setStatusCounts);
    }
  }, [dateRangeState]); // Only depend on dateRangeState, not appliedFilters


  // Search reset
  useEffect(() => {
    if (searchKeyword.length === 0) {
      setIsSearchActive(false);
      setIsSearching(false);
      setPage(1);
      fetchBookingsData(null, null, null, 1, null, true).catch(error => {
        console.error('Search reset error:', error);
        handleApiError(error, 'Failed to reset search');
      });
    }
  }, [searchKeyword, fetchBookingsData]);

  // Pagination sync
  useEffect(() => {
    if (paginationInfo?.current_page && paginationInfo.current_page !== page) {
      setPage(paginationInfo.current_page);
    }
  }, [paginationInfo?.current_page]);

  // Main data fetching effect - handles filters, date range, and pagination changes
  useEffect(() => {
    // Skip if this is just a search state change - search has its own effect
    if (isSearchActive || isFilterUpdateInProgress) {
      return;
    }
    
    // Fetch the bookings if we have date range state
    if (!isDetailsRoute && dateRangeState) {
      // Reset to page 1 when filters change
      setPage(1);
      // Use current appliedFilters instead of null
      fetchBookingsData(null, appliedFilters, dateRangeState, 1, null).catch(error => {
        console.error('Main data fetch error:', error);
        handleApiError(error, 'Failed to load data');
      });
    }
  }, [dateRangeState, appliedFilters]); // Removed error state dependencies to prevent infinite loops

  // Handle filters
  const handleApplyFilters = useCallback((newFilters) => {
    // Extract values from filter objects before sending to backend
    const processedFilters = { ...newFilters };
    
    // Fields that need value extraction
    const fieldsToProcess = ['agents', 'leads', 'projects'];
    
    fieldsToProcess.forEach(field => {
      if (processedFilters[field] && Array.isArray(processedFilters[field])) {
        processedFilters[field] = processedFilters[field].map(item => {
          // If item is an object with 'value' property, extract the value
          if (typeof item === 'object' && item !== null && 'value' in item) {
            return item.value;
          }
          // Otherwise return as-is
          return item;
        });
      }
    });
    
    setAppliedFilters(processedFilters);
    setPage(1);
    setIsFilterUpdateInProgress(true);
    fetchBookingsData(searchKeyword, processedFilters, null, 1, null)
      .catch(error => {
        console.error('Apply filters error:', error);
        handleApiError(error, 'Failed to apply filters');
      })
      .finally(() => {
        setIsFilterUpdateInProgress(false);
      });
  }, [setAppliedFilters, searchKeyword, fetchBookingsData, handleApiError]);

  
  // Function to clear applied filters and fetch all bookings
  const handleResetFilters = useCallback(() => {
    // Clear all filters in context
    setAppliedFilters({}); 
    
    // Reset search
    setSearchKeyword('');
    setIsSearchActive(false);
    setIsSearching(false);
    
    // Reset page
    setPage(1);
    
    // Reset sidebar selection to 'all'
    setSelectedSidebarStatus('all');
    
    // Keep current date range state (don't change all_results flag)
    // Fetch bookings with current date range settings and refresh status counts
    Promise.all([
      fetchBookingsData(null, {}, dateRangeState, 1, rowsPerPage, true),
      fetchBookingStatusCounts(dateRangeState).then(setStatusCounts)
    ]).catch(error => {
      console.error('Error resetting filters:', error);
      handleApiError(error, 'Failed to reset filters');
    });
    
    // Optionally close the filter popover if it's open
    setFilterAnchorEl(null);
  }, [setAppliedFilters, dateRangeState, fetchBookingsData, rowsPerPage, fetchBookingStatusCounts, setStatusCounts, handleApiError]);

  // Calculate active filters and their display names
  const activeFilters = useMemo(() => {
    const entries = Object.entries(appliedFilters || {});
    const filters = [];
    
    console.log('Calculating active filters from:', appliedFilters);
    
    entries.forEach(([key, value]) => {
      // Skip is_deleted as it's not a user-applied filter
      if (key === 'is_deleted') return;
      
      console.log(`Processing filter key: ${key}, value:`, value);
      
      // Handle booking_amount filter specially since it's an object
      if (key === 'booking_amount' && typeof value === 'object') {
        if (value && (
          (value.min_value !== undefined && value.min_value !== 0) || 
          (value.max_value !== undefined && value.max_value !== 0 && value.max_value !== Infinity)
        )) {
          filters.push('Booking Amount');
          console.log('Added Booking Amount filter');
        }
      }
      // For arrays, check if they have content
      else if (Array.isArray(value) && value.length > 0) {
        const filterNames = {
          'status': 'Status',
          'payment_status': 'Payment Status',
          'agents': 'Agent',
          'agent_id': 'Agent',
          'projects': 'Project',
          'leads': 'Lead'
        };
        const filterName = filterNames[key] || key;
        filters.push(filterName);
        console.log(`Added ${filterName} filter`);
      }
      // For other objects, check if they're not empty
      else if (value && typeof value === 'object' && Object.keys(value).length > 0) {
        const filterNames = {
          'status': 'Status',
          'payment_status': 'Payment Status',
          'agents': 'Agent',
          'projects': 'Project',
          'leads': 'Lead'
        };
        const filterName = filterNames[key] || key;
        filters.push(filterName);
        console.log(`Added ${filterName} filter`);
      }
      // For other values, check if they're truthy and not empty strings
      else if (value !== null && value !== undefined && value !== '' && value !== false) {
        const filterNames = {
          'status': 'Status',
          'payment_status': 'Payment Status',
          'agents': 'Agent',
          'agent_id': 'Agent',
          'projects': 'Project',
          'leads': 'Lead'
        };
        const filterName = filterNames[key] || key;
        filters.push(filterName);
        console.log(`Added ${filterName} filter`);
      }
    });
    
    console.log('Final active filters:', filters);
    return filters;
  }, [appliedFilters]);

  const activeFilterCount = activeFilters.length;

  // Format count
  const formatResultsCount = (count) => count.toLocaleString();

  // Retry function
  const handleRetry = useCallback(() => {
    clearError();
    fetchBookingsData(null, appliedFilters, dateRangeState, page, rowsPerPage, true).catch(error => {
      console.error('Retry error:', error);
      handleApiError(error, 'Failed to retry');
    });
  }, [fetchBookingsData, appliedFilters, dateRangeState, page, rowsPerPage, clearError, handleApiError]);

  // Status filter from list header (status count pills)
  const handleStatusFilterChange = useCallback((statusKey) => {
    setAppliedFilters(prev => ({
      ...prev,
      status: statusKey === 'all' || !statusKey ? undefined : [statusKey],
    }));
    setPage(1);
  }, [setAppliedFilters]);

  // Status update handler
  const handleStatusUpdate = (booking, action) => {
    setSelectedBooking(booking);
    if (action === 'pending') {
      setAmountDialogOpen(true);
    } else {
      setSingleNoteBookingId(booking.id);
      setCurrentAction(action);
    }
  };

  // Save finance
  const addBookingCharges = (booking) => {
    console.log('Booking finance updated:', booking);
    setAmountDialogOpen(false);
  };

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <>
      <Box sx={{ display: 'flex', height: '100vh', overflowX: 'hidden' }}>
        {/* Sidebar */}
        {/* <BookingStatusSidebar
          width={sidebarCollapsed ? 10 : 200}
          collapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
          statusCounts={statusCounts}
          dateRangeState={dateRangeState}
          selectedStatus={selectedSidebarStatus}
          onStatusChange={setSelectedSidebarStatus}
        /> */}
        
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflowX: 'hidden' }}>
          {/* Top Bar */}
          <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
            {/* Error Display */}
            <ErrorDisplay
              message={error?.message}
              type={error?.type || 'error'}
              onRetry={handleRetry}
              showRetry={hasError && !!error?.message}
              showClose={false}
              sx={{ mb: 2 }}
            />
            
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <DateRangeFilter onChange={setDateRangeState} />
            </Stack>

            {/* Filters + Search + Pagination */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 1.5, mb: 1 }}>
              <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                {/* Filter Button */}
                <Tooltip title="Click to apply filters" placement="bottom">
                  <Button
                    size="medium"
                    startIcon={<FilterList />}
                    variant={activeFilterCount > 0 ? "contained" : "outlined"}
                    onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                  >
                    Filters
                    {activeFilterCount > 0 && (
                      <Badge 
                        badgeContent={activeFilterCount} 
                        color="secondary" 
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Button>
                </Tooltip>

                {/* Reset Filters - next to Filters, before search */}
                {activeFilterCount > 0 && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleResetFilters}
                    sx={{
                      height: '32px',
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderRadius: 1,
                      backgroundColor: 'error.light',
                      color: 'white',
                      fontWeight: 'bold',
                      border: 'none',
                      '&:hover': {
                        backgroundColor: 'error.main',
                        color: 'white',
                      }
                    }}
                  >
                    Reset Filters
                  </Button>
                )}

                {/* Search */}
                <TextField
                  size="small"
                  placeholder="Search by booking ID, customer, project..."
                  sx={{ minWidth: '400px' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchKeyword && typeof searchKeyword === 'string' && searchKeyword.trim()) {
                      setIsSearchActive(true);
                      setIsSearching(true);
                      fetchBookingsData(searchKeyword, null, null, 1, null).finally(() => {
                        setIsSearching(false);
                      });
                    }
                  }}
                />
              </Box>

              {/* Pagination */}
              <Pagination
                totalCount={paginationInfo?.total_count || 0}
                currentPage={page}
                pageSize={rowsPerPage}
                totalPages={paginationInfo?.total_pages || 1}
                onPageChange={(newPage) => {
                  setPage(newPage);
                  fetchBookingsData(null, appliedFilters, dateRangeState, newPage, rowsPerPage, true).catch(handleApiError);
                }}
                onPageSizeChange={(newSize) => {
                  setRowsPerPage(newSize);
                  setPage(1);
                  fetchBookingsData(null, appliedFilters, dateRangeState, 1, newSize, true).catch(handleApiError);
                }}
                formatResultsCount={formatResultsCount}
              />
            </Box>
          </Box>

          {/* Booking stats bar (full width, status filter) */}
          <Box sx={{ width: '100%', px: 1 }}>
            <BookingStatsBar
              statusCounts={statusCounts}
              loading={loading}
              onFilterChange={handleStatusFilterChange}
              activeFilter={appliedFilters?.status?.[0] ?? 'all'}
            />
          </Box>

          {/* Bookings List — only table area scrolls horizontally (overflowX in BookingsListView) */}
          <Box sx={{ flex: 1, overflowX: 'hidden', overflowY: 'auto', minHeight: 0, p: 1 }}>
            <BookingsListView
              bookings={bookings || []}
              isLoading={loading}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={paginationInfo?.total_count ?? 0}
              totalPages={paginationInfo?.total_pages ?? 1}
              onPageChange={(p) => { setPage(p); fetchBookingsData(null, appliedFilters, dateRangeState, p, rowsPerPage, true).catch(handleApiError); }}
              onRowsPerPageChange={(size) => { setRowsPerPage(size); setPage(1); fetchBookingsData(null, appliedFilters, dateRangeState, 1, size, true).catch(handleApiError); }}
              onStatusUpdate={handleStatusUpdate}
              onSelectBooking={setSelectedBooking}
              onCreateBooking={() => setCreateBookingOpen(true)}
              statusCounts={statusCounts}
              activeStatusFilter={appliedFilters?.status?.[0] ?? 'all'}
              onStatusFilterChange={handleStatusFilterChange}
              activeProjectFilter={appliedFilters?.projects?.[0] ?? null}
              onProjectFilterChange={(projectId) => {
                setAppliedFilters(prev => ({ ...prev, projects: projectId == null ? undefined : [projectId] }));
                setPage(1);
              }}
              sidebarProjectCounts={sidebarProjectCounts}
              onSearchChange={(term) => { setSearchKeyword(term); setIsSearchActive(!!(term && term.trim())); fetchBookingsData(term && term.trim() ? term.trim() : null, appliedFilters, dateRangeState, 1, rowsPerPage, true).catch(handleApiError); }}
              hideTablePagination
            />
          </Box>
        </Box>
      </Box>

      {/* Filter Dialog */}
      <BookingFilter
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        onApply={(filters) => {
          handleApplyFilters(filters);
          setFilterAnchorEl(null);
        }}
        filters={appliedFilters}
        agents={referenceData.agents}
        leads={referenceData.leads}
        projects={referenceData.projects}
        booking_statuses={referenceData.bookingStatusOptions}
        payment_statuses={referenceData.paymentStatusOptions}
      />

      {/* Create Booking */}
      <CreateNewBooking
        initialData={selectedBooking}
        open={createBookingOpen}
        onClose={() => setCreateBookingOpen(false)}
        leads={referenceData.leads || []}
        units={referenceData.units || {}}
        filtered_projects={referenceData.filtered_projects || []}
      />

      {/* Notes */}
      {singleNoteBookingId && (
        <BookingNotes
          open
          onClose={() => {
            setSingleNoteBookingId(null);
            setCurrentAction(null);
          }}
          bookingId={singleNoteBookingId}
          action={currentAction}
          onComplete={() => setSingleNoteBookingId(null)}
          booking={selectedBooking}
        />
      )}

      {/* Finance */}
      <AddBookingFinance  
        open={amountDialogOpen}
        onClose={() => setAmountDialogOpen(false)}
        booking={selectedBooking}
        onSave={fetchBookingsData}
        otherCharges={referenceData.other_charges || []}
      />
    </>
  );
};

export default Bookings;
